import { initializePaddle, type Paddle } from '@paddle/paddle-js';
import {
  json,
  redirect,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
} from '@remix-run/node';
import { Link, useFetcher, useLoaderData } from '@remix-run/react';
import { useEffect, useState } from 'react';
// import { getClientIPAddress } from 'remix-utils/get-client-ip-address';
import { promiseHash } from 'remix-utils/promise';
import { wait } from 'remix-utils/timers';
import { z } from 'zod';
import { Button } from '#app/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '#app/components/ui/card.tsx';
import { Icon } from '#app/components/ui/icon.tsx';
import { getUserAccessLevel, getUserId } from '#app/utils/auth.server.ts';
import { paddle } from '#app/utils/paddle.server.ts';
import { redirectWithToast } from '#app/utils/toast.server.js';
import { useOptionalUser } from '#app/utils/user.ts';

const ProvisionAccountSchema = z.object({
  transactionId: z.string(),
});

// TODO: Based on ip address
export async function loader({ request }: LoaderFunctionArgs) {
  const accessLevel = await getUserAccessLevel(request);

  // You shouldn't be able to re-purchase
  // a subscription or lifetime membership
  if (accessLevel === 'paid-tier') {
    throw redirect('/dashboard');
  }

  return json(
    await promiseHash({
      proPlan: paddle.products.get(process.env.PADDLE_PRO_PLAN_ID, {
        include: ['prices'],
      }),
      lifetimePlan: paddle.products.get(process.env.PADDLE_LIFETIME_PLAN_ID, {
        include: ['prices'],
      }),
    }),
  );
}

export async function action({ request }: ActionFunctionArgs) {
  const userId = await getUserId(request);
  if (!userId) {
    return json({ message: 'NOT_AUTHORIZED' }, { status: 404 });
  }

  const body = ProvisionAccountSchema.safeParse(await request.json());
  if (!body.success) {
    return json({ message: 'BAD_REQUEST' }, { status: 400 });
  }

  try {
    await paddle.transactions.get(body.data.transactionId);
  } catch (error) {
    return json({ message: 'BAD_REQUEST' }, { status: 400 });
  }

  let attempt = 0;
  while (attempt < 5) {
    await wait(2000);

    const accessLevel = await getUserAccessLevel(request);

    if (accessLevel === 'paid-tier') {
      return redirectWithToast('/dashboard', {
        type: 'success',
        description: 'Full Access Unlocked!',
      });
    }

    attempt += 1;
  }

  return json({ message: 'Failed' }, { status: 500 });
}

export default function PricingPage() {
  const { proPlan, lifetimePlan } = useLoaderData<typeof loader>();
  const user = useOptionalUser();
  const [paddle, setPaddle] = useState<Paddle>();
  const fetcher = useFetcher();

  useEffect(() => {
    async function initPaddle() {
      const _paddle = await initializePaddle({
        environment: ENV.PADDLE_ENVIRONMENT,
        token: ENV.PADDLE_CLIENT_TOKEN,
        eventCallback: (event) => {
          if (event.name === 'checkout.completed') {
            _paddle?.Checkout.close();

            fetcher.submit(
              { transactionId: event.data?.transaction_id ?? '' },
              { method: 'POST', encType: 'application/json' },
            );
          }
        },
      });

      setPaddle(_paddle);
    }

    initPaddle();
  }, [fetcher]);

  return (
    <main className="container flex h-full flex-col gap-8">
      {fetcher.state !== 'idle' ? (
        <div className="absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center bg-black/80">
          <Icon name="update" className="h-12 w-12 animate-spin" />
        </div>
      ) : null}

      <h1 className="text-center text-2xl font-bold">Kempo Pricing</h1>

      <div className="mb-5 flex flex-1 flex-col gap-5 lg:flex-row">
        <Card className="flex flex-1 flex-col gap-4">
          <CardHeader>
            <CardTitle>Prototype</CardTitle>
            <CardDescription className="h-[40px]">
              Try Kempo without any commitment
            </CardDescription>
            <span className="text-4xl">Free</span>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Button asChild>
              {user ? (
                <Link to="/dashboard">Dashboard</Link>
              ) : (
                <Link to="/signup">Sign Up</Link>
              )}
            </Button>
            <span className="text-lg font-bold">Plan details:</span>
            <FeatureList
              features={[
                { feature: 'Basic Templates' },
                { feature: 'Basic Components' },
                { feature: 'Custom Layouts' },
                { feature: '10 Projects' },
              ]}
            />
          </CardContent>
        </Card>

        {[proPlan, lifetimePlan].map((product) => {
          const price = product.prices?.[0];
          if (!price) return null;

          const normalizedPrice =
            price.unitPrice.currencyCode === 'USD'
              ? `$${Number(price.unitPrice.amount) / 100}`
              : null;

          const normalizedCycle =
            price.billingCycle?.interval === 'month' ? 'mo' : null;

          if (!normalizedPrice) return null;

          return (
            <Card key={product.id} className="flex flex-1 flex-col gap-4">
              <CardHeader>
                <CardTitle>{product.name}</CardTitle>
                <CardDescription className="h-[40px]">
                  {product.description}
                </CardDescription>
                <span className="text-4xl">
                  {normalizedPrice}{' '}
                  {normalizedCycle ? `/${normalizedCycle}` : null}
                </span>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {user ? (
                  <Button
                    disabled={!paddle}
                    onClick={() => {
                      paddle?.Checkout.open({
                        customData: { userId: user.id },
                        items: [{ priceId: price?.id, quantity: 1 }],
                      });
                    }}
                  >
                    {normalizedCycle ? 'Subscribe' : 'Purchase'}
                  </Button>
                ) : (
                  <Button asChild>
                    <Link to="/signup">
                      Sign Up
                      <span className="sr-only">
                        to {normalizedCycle ? 'Subscribe' : 'Purchase'}
                      </span>
                    </Link>
                  </Button>
                )}
                <span className="text-lg font-bold">Plan details:</span>
                <span>Everything in the prototype plan, plus:</span>
                <FeatureList
                  features={[
                    { feature: 'Custom Themes' },
                    { feature: 'Custom Branding' },
                    { feature: 'Unlimited Projects', className: 'font-bold' },
                  ]}
                />
              </CardContent>
            </Card>
          );
        })}
      </div>
    </main>
  );
}

type FeatureListProps = {
  features: Array<{ feature: string; className?: string }>;
};

function FeatureList(props: FeatureListProps) {
  return (
    <ul className="flex flex-col gap-2">
      {props.features.map(({ feature, className }) => (
        <li key={feature} className={className}>
          <Icon name="check-circled" className="text-green-500">
            {feature}
          </Icon>
        </li>
      ))}
    </ul>
  );
}
